/**
 * Branding API Service
 * Fetches organization branding configuration from the backend
 */

import { BASE_URL, fetchWithAuth } from './client';
import { getMainSiteUrl } from '../../shared/utils/subdomain';

/**
 * Default branding configuration for CloudMortgage main site
 */
export const DEFAULT_BRANDING = {
    companyName: 'CloudMortgage.io',
    logoUrl: null, // Will use default Logo component
    logoDarkUrl: null,
    primaryColor: '#0284c7', // sky-600
    secondaryColor: '#0ea5e9', // sky-500
    accentColor: '#38bdf8', // sky-400
    loginTitle: 'Sign in to your account',
    loginSubtitle: null,
    loginBackgroundUrl: null,
    welcomeMessage: null,
    supportEmail: 'support@cloudmortgage.io',
    supportPhone: null,
    faviconUrl: null,
    privacyUrl: 'https://cloudmortgage.io/privacy',
    termsUrl: 'https://cloudmortgage.io/terms',
    ssoEnabled: false,
    ssoProvider: null,
    enforceSso: false,
};

/**
 * Fetch organization branding by slug (public endpoint - no auth required)
 * First looks up org_id from slug, then fetches public branding
 * Redirects to main site on 404 (organization not found)
 *
 * @param {string} slug - The organization slug from subdomain
 * @returns {Promise<Object>} The branding configuration
 */
export const fetchOrgBranding = async (slug) => {
    if (!slug) {
        return { ...DEFAULT_BRANDING };
    }

    try {
        // Step 1: Lookup org_id from slug
        const lookupResponse = await fetch(`${BASE_URL}/api/organizations/lookup/${encodeURIComponent(slug)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (lookupResponse.status === 404) {
            // Organization not found - redirect to main site
            console.warn(`Organization "${slug}" not found. Redirecting to main site.`);
            const mainSiteUrl = getMainSiteUrl();
            window.location.href = mainSiteUrl;
            // Return default while redirect happens
            return { ...DEFAULT_BRANDING };
        }

        if (!lookupResponse.ok) {
            throw new Error(`Failed to lookup organization: ${lookupResponse.status}`);
        }

        const lookupData = await lookupResponse.json();
        const orgId = lookupData.org_id || lookupData.id;

        if (!orgId) {
            throw new Error('Organization ID not found in lookup response');
        }

        // Step 2: Fetch public branding using org_id
        const brandingResponse = await fetch(`${BASE_URL}/api/organizations/${orgId}/branding/public`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // If public branding endpoint fails, fall back to lookup data
        let data = lookupData;
        if (brandingResponse.ok) {
            const brandingData = await brandingResponse.json();
            // Merge branding data with lookup data (branding takes precedence)
            data = { ...lookupData, ...brandingData };
        } else {
            console.warn(`Public branding endpoint failed (${brandingResponse.status}), using lookup data`);
        }

        // Merge with defaults to ensure all fields are present
        return {
            ...DEFAULT_BRANDING,
            orgId: data.org_id || data.id || data.orgId || orgId,
            slug: data.slug || slug,
            companyName: data.company_name || data.name || data.companyName || DEFAULT_BRANDING.companyName,
            logoUrl: data.logo_url || data.logoUrl || data.logo || DEFAULT_BRANDING.logoUrl,
            logoDarkUrl: data.logo_dark_url || data.logoDarkUrl || DEFAULT_BRANDING.logoDarkUrl,
            primaryColor: data.primary_color || data.primaryColor || data.brandColor || DEFAULT_BRANDING.primaryColor,
            secondaryColor: data.secondary_color || data.secondaryColor || DEFAULT_BRANDING.secondaryColor,
            accentColor: data.accent_color || data.accentColor || DEFAULT_BRANDING.accentColor,
            loginTitle: data.login_title || data.loginTitle || `Sign in to ${data.company_name || data.name || 'your account'}`,
            loginSubtitle: data.login_subtitle || data.loginSubtitle || DEFAULT_BRANDING.loginSubtitle,
            loginBackgroundUrl: data.login_background_url || data.loginBackgroundUrl || DEFAULT_BRANDING.loginBackgroundUrl,
            welcomeMessage: data.welcome_message || data.welcomeMessage || DEFAULT_BRANDING.welcomeMessage,
            supportEmail: data.support_email || data.supportEmail || DEFAULT_BRANDING.supportEmail,
            supportPhone: data.support_phone || data.supportPhone || DEFAULT_BRANDING.supportPhone,
            faviconUrl: data.favicon_url || data.faviconUrl || data.favicon || DEFAULT_BRANDING.faviconUrl,
            privacyUrl: data.privacy_url || data.privacyUrl || DEFAULT_BRANDING.privacyUrl,
            termsUrl: data.terms_url || data.termsUrl || DEFAULT_BRANDING.termsUrl,
            // SSO info (from lookup endpoint)
            ssoEnabled: data.sso_enabled || data.ssoEnabled || false,
            ssoProvider: data.sso_provider || data.ssoProvider || null,
            enforceSso: data.enforce_sso || data.enforceSso || false,
        };
    } catch (error) {
        console.error('Error fetching organization branding:', error);
        // Return default branding on error
        return { ...DEFAULT_BRANDING };
    }
};

/**
 * Apply branding colors as CSS custom properties to the document
 *
 * @param {Object} branding - The branding configuration
 */
export const applyBrandingColors = (branding) => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.style.setProperty('--color-primary', branding.primaryColor);
    root.style.setProperty('--color-secondary', branding.secondaryColor);
    root.style.setProperty('--color-accent', branding.accentColor);
};

/**
 * Update the page favicon
 *
 * @param {string} faviconUrl - URL of the favicon
 */
export const updateFavicon = (faviconUrl) => {
    if (typeof document === 'undefined' || !faviconUrl) return;

    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'shortcut icon';
        document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = faviconUrl;
};

/**
 * Update the page title
 *
 * @param {string} companyName - The company name to use in title
 */
export const updatePageTitle = (companyName) => {
    if (typeof document === 'undefined') return;

    const baseTitle = 'Lending Platform';
    document.title = companyName ? `${companyName} | ${baseTitle}` : `CloudMortgage.io | ${baseTitle}`;
};

/**
 * Get full branding settings (for settings page)
 * Uses /branding/public endpoint for reading
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>}
 */
export const getBrandingSettings = async (orgId) => {
    const response = await fetchWithAuth(`/api/organizations/${orgId}/branding/public`);
    if (!response.ok) {
        throw new Error('Failed to fetch branding settings');
    }
    const data = await response.json();
    return {
        logoUrl: data.logo_url || null,
        logoDarkUrl: data.logo_dark_url || null,
        faviconUrl: data.favicon_url || null,
        primaryColor: data.primary_color || '#0284c7',
        secondaryColor: data.secondary_color || '#0ea5e9',
        accentColor: data.accent_color || '#38bdf8',
        loginTitle: data.login_title || '',
        loginSubtitle: data.login_subtitle || '',
        loginBackgroundUrl: data.login_background_url || null,
        welcomeMessage: data.welcome_message || '',
        companyName: data.company_name || '',
        supportEmail: data.support_email || '',
        supportPhone: data.support_phone || '',
        privacyUrl: data.privacy_url || '',
        termsUrl: data.terms_url || '',
        customCss: data.custom_css || '',
    };
};

/**
 * Update branding settings
 * @param {string} orgId - Organization ID
 * @param {Object} settings - Branding settings to update
 * @returns {Promise<Object>}
 */
export const updateBrandingSettings = async (orgId, settings) => {
    const response = await fetchWithAuth(`/api/organizations/${orgId}/branding`, {
        method: 'PUT',
        body: JSON.stringify({
            logo_url: settings.logoUrl,
            logo_dark_url: settings.logoDarkUrl,
            favicon_url: settings.faviconUrl,
            primary_color: settings.primaryColor,
            secondary_color: settings.secondaryColor,
            accent_color: settings.accentColor,
            login_title: settings.loginTitle,
            login_subtitle: settings.loginSubtitle,
            login_background_url: settings.loginBackgroundUrl,
            welcome_message: settings.welcomeMessage,
            company_name: settings.companyName,
            support_email: settings.supportEmail,
            support_phone: settings.supportPhone,
            privacy_url: settings.privacyUrl,
            terms_url: settings.termsUrl,
            custom_css: settings.customCss,
        }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to update branding');
    }
    return response.json();
};

/**
 * Get SSO configuration
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>}
 */
export const getSsoConfig = async (orgId) => {
    const response = await fetchWithAuth(`/api/organizations/${orgId}/sso`);
    if (response.status === 404) {
        return null; // SSO not configured
    }
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('UPGRADE_REQUIRED');
        }
        throw new Error('Failed to fetch SSO config');
    }
    const data = await response.json();
    return {
        provider: data.provider,
        enabled: data.enabled,
        clientId: data.client_id,
        issuerUrl: data.issuer_url,
        enforceSso: data.enforce_sso,
        autoProvisionUsers: data.auto_provision_users,
        defaultRole: data.default_role,
        allowedDomains: data.allowed_domains || [],
        createdAt: data.created_at,
        updatedAt: data.updated_at,
    };
};

/**
 * Create or update SSO configuration
 * @param {string} orgId - Organization ID
 * @param {Object} config - SSO configuration
 * @param {boolean} isUpdate - Whether this is an update (PUT) or create (POST)
 * @returns {Promise<Object>}
 */
export const saveSsoConfig = async (orgId, config, isUpdate = false) => {
    const response = await fetchWithAuth(`/api/organizations/${orgId}/sso`, {
        method: isUpdate ? 'PUT' : 'POST',
        body: JSON.stringify({
            provider: config.provider,
            client_id: config.clientId,
            client_secret: config.clientSecret,
            issuer_url: config.issuerUrl,
            enabled: config.enabled,
            enforce_sso: config.enforceSso,
            auto_provision_users: config.autoProvisionUsers,
            default_role: config.defaultRole,
            allowed_domains: config.allowedDomains,
        }),
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('UPGRADE_REQUIRED');
        }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to save SSO config');
    }
    return response.json();
};

/**
 * Delete SSO configuration
 * @param {string} orgId - Organization ID
 * @returns {Promise<void>}
 */
export const deleteSsoConfig = async (orgId) => {
    const response = await fetchWithAuth(`/api/organizations/${orgId}/sso`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error('Failed to delete SSO config');
    }
};

/**
 * Test SSO connection
 * @param {string} orgSlug - Organization slug
 * @returns {string} - URL to open for testing SSO
 */
export const getSsoTestUrl = (orgSlug) => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/dashboard`);
    return `${BASE_URL}/api/auth/sso/${orgSlug}/login?redirect_uri=${redirectUri}`;
};

/**
 * SSO Providers list
 */
export const SSO_PROVIDERS = [
    { id: 'okta', name: 'Okta', icon: '🔐' },
    { id: 'azure_ad', name: 'Azure AD', icon: '🔷' },
    { id: 'google_workspace', name: 'Google Workspace', icon: '🔴' },
    { id: 'onelogin', name: 'OneLogin', icon: '🔑' },
    { id: 'generic_oidc', name: 'Generic OIDC', icon: '🔗' },
];

/**
 * Upload an image for branding (logo, favicon, background)
 * Backend auto-updates org branding settings after upload
 * @param {string} orgId - Organization ID
 * @param {File} file - The image file to upload
 * @param {string} type - Type of image: 'logo', 'logo_dark', 'favicon', 'background'
 * @returns {Promise<{url: string}>} - The uploaded image URL
 */
export const uploadBrandingImage = async (orgId, file, type = 'logo') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/branding/upload`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for FormData
        headers: {},
    });

    if (!response.ok) {
        if (response.status === 413) {
            throw new Error('File is too large. Maximum size is 5MB.');
        }
        if (response.status === 415) {
            throw new Error('Invalid file type. Please upload PNG, JPG, GIF, SVG, or ICO.');
        }
        if (response.status === 403) {
            throw new Error('You do not have permission to upload branding images.');
        }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail?.message || error.detail || 'Failed to upload image');
    }

    const data = await response.json();
    return { url: data.url || data.image_url };
};

/**
 * Delete a branding image
 * Clears URL from org branding settings
 * @param {string} orgId - Organization ID
 * @param {string} imageType - Type of image: 'logo', 'logo_dark', 'favicon', 'background'
 * @returns {Promise<void>}
 */
export const deleteBrandingImage = async (orgId, imageType) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/branding/images/${imageType}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to delete branding images.');
        }
        if (response.status === 404) {
            // Image doesn't exist, that's fine
            return;
        }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail?.message || error.detail || 'Failed to delete image');
    }
};

export default {
    fetchOrgBranding,
    applyBrandingColors,
    updateFavicon,
    updatePageTitle,
    getBrandingSettings,
    updateBrandingSettings,
    uploadBrandingImage,
    deleteBrandingImage,
    getSsoConfig,
    saveSsoConfig,
    deleteSsoConfig,
    getSsoTestUrl,
    SSO_PROVIDERS,
    DEFAULT_BRANDING,
};
